import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  message,
  Popconfirm,
  Space,
  Tag,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type PublicRoomTask,
  publicRoomPreparationApi,
} from '@/services/publicRoomsApi';
import {
  taskPlatformApi,
  taskPlatformErrorMessage,
} from '@/services/taskPlatformApi';
import type { TaskExecution } from '@/types/taskPlatform';
import { publicationTaskState } from './publicationTaskState';

const labels = {
  preparing: '准备文件中',
  confirming: '业务已完成，确认结果中',
  ready: '任务已完成',
  failed: '任务失败',
  notificationFailed: '业务已完成，结果通知失败',
  unknown: '状态待确认',
};
export function PublicationTaskDrawer({
  task,
  open,
  close,
  completed,
  retry,
}: {
  task: PublicRoomTask | undefined;
  open: boolean;
  close: () => void;
  completed: () => void;
  retry: (task: PublicRoomTask) => void;
}) {
  const [value, setValue] = useState<TaskExecution>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const completedRef = useRef(completed);
  completedRef.current = completed;
  useEffect(() => {
    if (!task) return;
    setValue(undefined);
    setError('');
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let refreshed = false;
    const poll = async () => {
      try {
        const next = await taskPlatformApi.getTask(task.taskId);
        if (stopped) return;
        setValue(next);
        setError('');
        const state = publicationTaskState(next);
        if (
          !refreshed &&
          (next.mainOutcome === 'succeeded' || state === 'failed')
        ) {
          refreshed = true;
          completedRef.current();
        }
        if (
          state === 'ready' ||
          state === 'failed' ||
          state === 'notificationFailed'
        )
          return;
      } catch (cause) {
        if (stopped) return;
        setError(
          taskPlatformErrorMessage(cause, '任务可能正在交接，稍后继续查询'),
        );
      }
      if (!stopped) timer = setTimeout(() => void poll(), 3000);
    };
    void poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [task]);
  const state = value ? publicationTaskState(value) : 'preparing';
  return (
    <Drawer
      title={task?.autoPublish ? '公开房间任务' : '文件缓存刷新任务'}
      open={open && !!task}
      onClose={close}
      width={520}
    >
      {task && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Tag
            color={
              state === 'ready'
                ? 'green'
                : state === 'failed' || state === 'notificationFailed'
                  ? 'red'
                  : 'blue'
            }
          >
            {labels[state]}
          </Tag>
          {error && <Alert type="warning" showIcon message={error} />}
          <Alert
            type={state === 'failed' ? 'error' : 'info'}
            showIcon
            message={
              state === 'notificationFailed'
                ? '业务已完成，请查看当前房间状态；通知失败不会自动下架。'
                : state === 'failed'
                  ? '准备或发布未完成，请查看当前房间状态和任务详情。'
                  : task.autoPublish
                    ? '产物目录内的文件及目录缓存准备成功后自动公开，房间状态以列表为准。'
                    : '刷新现有缓存，保持当前房间公开状态。'
            }
          />
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="任务">
              <span className="break-all">{task.taskId}</span>
            </Descriptions.Item>
            <Descriptions.Item label="房间">{task.roomId}</Descriptions.Item>
          </Descriptions>
          <Space wrap>
            <Link to={`/tasks/executions/${task.taskId}`}>查看任务详情</Link>
            {state === 'failed' && (
              <Button onClick={() => retry(task)}>重新发起</Button>
            )}
            {(state === 'preparing' || state === 'unknown') && (
              <Popconfirm
                title="取消本房间当前版本的待执行任务？"
                description="会阻止尚未提交的发布；已经完成的公开需另外下架。"
                onConfirm={async () => {
                  setBusy(true);
                  try {
                    await publicRoomPreparationApi.cancel(
                      task.roomId,
                      task.expectedUpdatedAtUnixMs,
                    );
                    message.success('已撤销当前版本的任务资格');
                    completedRef.current();
                    close();
                  } catch (cause) {
                    message.error(
                      taskPlatformErrorMessage(
                        cause,
                        '房间状态已变化，请刷新确认',
                      ),
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <Button loading={busy}>取消</Button>
              </Popconfirm>
            )}
            <Button onClick={completed}>刷新房间状态</Button>
          </Space>
        </Space>
      )}
    </Drawer>
  );
}
